import { z } from "zod";
import {
  baseToolPatchPayloadSchema,
  baseToolPostPayloadSchema
} from "./base.js";
const WebDocumentResearchToolType = "web_document_research";
const webDocumentResearchToolMaxSourceCount = 5;
const webDocumentResearchToolTimeoutMs = 3e4;
const webDocumentResearchToolMaxSourceUrlLength = 2048;
const webDocumentResearchToolMaxAllowedDomainCount = 20;
const webDocumentResearchToolMaxAllowedDomainLength = 253;
const domainLabelPattern = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/u;
function normalizeAllowedDomain(domain) {
  return domain.trim().toLowerCase();
}
function checkIsIpv4Literal(hostname) {
  const labels = hostname.split(".");
  return labels.length === 4 && labels.every(
    (label) => /^\d{1,3}$/u.test(label) && Number(label) >= 0 && Number(label) <= 255
  );
}
function checkIsValidAllowedDomain(domain) {
  const normalized = normalizeAllowedDomain(domain);
  if (normalized.length === 0 || normalized.length > webDocumentResearchToolMaxAllowedDomainLength || normalized.endsWith(".") || checkIsIpv4Literal(normalized)) {
    return false;
  }
  const labels = normalized.split(".");
  return labels.length >= 2 && labels.every((label) => domainLabelPattern.test(label));
}
const webDocumentResearchToolAllowedDomainSchema = z.string().transform(normalizeAllowedDomain).pipe(
  // The check also covers the empty and over-length cases, so every failure
  // reports the same actionable message.
  z.string().refine(checkIsValidAllowedDomain, {
    message: "Enter a hostname only, without a scheme, path, port, wildcard, or IP address."
  })
);
const webDocumentResearchToolAllowedDomainsSchema = z.array(webDocumentResearchToolAllowedDomainSchema).min(1, "Add at least one allowed domain.").max(
  webDocumentResearchToolMaxAllowedDomainCount,
  `Add no more than ${webDocumentResearchToolMaxAllowedDomainCount.toString()} allowed domains.`
).transform((domains) => Array.from(new Set(domains)));
const webDocumentResearchToolSettingsSchema = z.object({
  allowed_domains: webDocumentResearchToolAllowedDomainsSchema
});
const webDocumentResearchToolSourceUrlSchema = z.string().max(
  webDocumentResearchToolMaxSourceUrlLength,
  `Source URLs must be ${webDocumentResearchToolMaxSourceUrlLength.toString()} characters or fewer.`
).superRefine((source, ctx) => {
  if (source !== source.trim() || !URL.canParse(source)) {
    ctx.addIssue({ code: "custom", message: "Enter a valid HTTPS URL." });
    return;
  }
  const parsed = new URL(source);
  if (parsed.protocol !== "https:") {
    ctx.addIssue({ code: "custom", message: "Source URLs must use HTTPS." });
  }
  if (parsed.username !== "" || parsed.password !== "") {
    ctx.addIssue({
      code: "custom",
      message: "Source URLs cannot contain credentials."
    });
  }
  if (parsed.port !== "") {
    ctx.addIssue({
      code: "custom",
      message: "Source URLs cannot use a custom port."
    });
  }
  if (!checkIsValidAllowedDomain(parsed.hostname)) {
    ctx.addIssue({
      code: "custom",
      message: "Source URLs must use a valid hostname, not an IP address."
    });
  }
  if (!parsed.pathname.toLowerCase().endsWith(".pdf")) {
    ctx.addIssue({
      code: "custom",
      message: "Source URLs must point to a PDF document."
    });
  }
});
const webDocumentResearchToolInputSchema = z.object({
  prompt: z.string().trim().min(1).describe(
    "A self-contained research request that explains what to find and how to return it."
  ),
  sources: z.array(webDocumentResearchToolSourceUrlSchema).min(1).max(
    webDocumentResearchToolMaxSourceCount,
    `Provide no more than ${webDocumentResearchToolMaxSourceCount.toString()} sources.`
  ).describe(
    "The PDF document URLs to research. Base the response only on these documents."
  )
});
function buildWebDocumentResearchToolParametersSchema(allowedDomains) {
  return {
    type: "object",
    properties: {
      prompt: {
        type: "string",
        description: "A self-contained research request that explains what to find and how to return it."
      },
      sources: {
        type: "array",
        items: { type: "string" },
        description: `One to ${webDocumentResearchToolMaxSourceCount.toString()} HTTPS URLs of PDF documents. Each hostname must exactly match an allowed domain: ${allowedDomains.join(", ")}.`
      }
    },
    required: ["prompt", "sources"],
    additionalProperties: false
  };
}
const webDocumentResearchToolPostPayloadSchema = baseToolPostPayloadSchema.extend({
  type: z.literal(WebDocumentResearchToolType),
  settings: webDocumentResearchToolSettingsSchema
});
const webDocumentResearchToolPatchPayloadSchema = baseToolPatchPayloadSchema.extend({
  type: z.literal(WebDocumentResearchToolType),
  settings: webDocumentResearchToolSettingsSchema.optional()
});
export {
  WebDocumentResearchToolType,
  buildWebDocumentResearchToolParametersSchema,
  webDocumentResearchToolAllowedDomainSchema,
  webDocumentResearchToolAllowedDomainsSchema,
  webDocumentResearchToolInputSchema,
  webDocumentResearchToolMaxAllowedDomainCount,
  webDocumentResearchToolMaxAllowedDomainLength,
  webDocumentResearchToolMaxSourceCount,
  webDocumentResearchToolMaxSourceUrlLength,
  webDocumentResearchToolPatchPayloadSchema,
  webDocumentResearchToolPostPayloadSchema,
  webDocumentResearchToolSettingsSchema,
  webDocumentResearchToolTimeoutMs
};
//# sourceMappingURL=webDocumentResearch.js.map
